<template>
  <va-input v-bind="$props" class="va-array-input" v-if="input">
    <div class="column-layout-without-drag" v-if="columnLayoutWithoutDrag">
      <div v-for="(item, i) in input" :key="item.id">
        <div class="d-flex">
          <div class="column-layout-without-drag__title">
            {{ blockLabel }} {{ i + 1 }}
          </div>

          <div class="column-layout-without-drag__delete-button">
            <v-btn
              :ripple="false"
              class="btn-custom-del"
              icon
              plain
              @click.stop="remove(i)"
            >
              <v-icon small @click.stop="remove(i)"
                >mdi-trash-can-outline</v-icon
              >
              {{ $t("va.actions.delete") }}
            </v-btn>
          </div>
        </div>

        <div>
          <slot
            :resource="resource"
            :item="item"
            :parent-source="source"
            :index="i"
            :array-input="true"
            :selected="input"
          ></slot>
        </div>
      </div>

      <v-btn class="btn-custom-add" outlined @click.stop="add">
        <v-icon small>mdi-plus</v-icon>
        <span>{{
          addButtonText ? addButtonText : $t("va.actions.add_menu_link")
        }}</span>
      </v-btn>
    </div>

    <draggable
      v-else
      :value="input"
      draggable=".item"
      handle=".handle"
      ghost-class="ghost"
      @input="update"
      @end="onDragEnd"
      @start="onDragStart"
    >
      <div v-for="(item, i) in input" :key="item.id" class="item relative">
        <div class="va-array-input__actions-wrapper">
          <div class="remove-action">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  :ripple="false"
                  v-on="on"
                  class="btn-custom-del"
                  icon
                  plain
                  @click.stop="remove(i)"
                >
                  <v-icon @click.stop="remove(i)">mdi-close</v-icon>
                </v-btn>
              </template>
              <span>{{ $t("va.actions.delete") }}</span>
            </v-tooltip>
          </div>
          <div class="drag-action">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <div icon text v-on="on" class="handle action-icon">
                  <img :src="dotsImg" v-on="on" alt="dots icon" />
                </div>
              </template>
              <span>{{ $t("va.actions.move") }}</span>
            </v-tooltip>
          </div>
        </div>
        <div class="d-flex align-start va-array-input__item-wrapper">
          <!--
            @slot Your repeatable group of inputs components.
            @binding {string} resource Name of resource.
            @binding {object} item Object item of array.
            @binding {string} parentSource Original array source.
            @binding {number} index Index of item inside array.
          -->
          <slot
            :resource="resource"
            :item="item"
            :parent-source="source"
            :index="i"
            :array-input="true"
            :selected="input"
          ></slot>
        </div>
      </div>

      <v-btn class="btn-custom-add" outlined @click.stop="add">
        <v-icon small>mdi-plus</v-icon>
        <span>{{
          addButtonText ? addButtonText : $t("va.actions.add_menu_link")
        }}</span>
      </v-btn>
    </draggable>
  </va-input>
</template>

<script>
import Input from "../../../mixins/input";
import dotsImg from "../../../assets/icons/6-dots.svg";

/**
 * Allow multiple embed inputs as array. See it as a repeater.
 * Support adding, removing, as well as moving via drag thanks to VueDraggable.
 */
export default {
  data() {
    return {
      dotsImg: dotsImg,
    };
  },
  mixins: [Input],
  props: {
    /**
     * List of object to edit.
     * @model
     */
    value: {
      type: Array,
      default: () => [],
    },
    addButtonText: {
      type: String,
      required: false,
      default: "",
    },
    columnLayoutWithoutDrag: {
      type: Boolean,
      required: false,
      default: false,
    },
    blockLabel: {
      type: String,
      required: false,
      default: "",
    },
    allowEmpty: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  beforeMount() {
    if (
      this.columnLayoutWithoutDrag &&
      !this.allowEmpty &&
      this.input.length === 0 &&
      this.value
    ) {
      this.add();
    }
  },
  methods: {
    getItem(value) {
      /**
       * Generate fake id for drag tracking
       */
      return (value || []).map((v, i) => {
        return { ...v, id: i };
      });
    },
    add() {
      // If no previous ID exist, latestId becomes -Infinity.
      const latestId = Math.max(...this.input.map((o) => o.id));
      const newId = isFinite(latestId) ? latestId + 1 : 1;
      this.input.push({ id: newId });
      this.update(this.input);
    },
    remove(index) {
      this.input.splice(index, 1);
      this.update(this.input);
    },
    onDragStart(e) {
      setTimeout(() => {
        e.target.classList.add("grabbing");
      }, 50);
    },
    onDragEnd(e) {
      e.target.classList.remove("grabbing");
    },
  },
};
</script>

<style lang="scss">
.ghost {
  &::after {
    content: " ";
    padding-bottom: 10px;
    border-bottom: 1px solid #7e5dd3;
  }
}

.va-array-input {
  & > .v-input__control > .v-input__slot {
    display: block;
  }
  & > .v-input__control > .v-input__slot > .v-label {
    display: inline-block;
    margin-bottom: 0.5rem;
  }
  &__actions-wrapper {
    height: 46px;
    position: absolute;
    inset: 0;
    top: 19px;
    .remove-action {
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
    }
    .drag-action {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
    }
  }
}

.item {
  display: flex;
  flex-direction: column;
  border-bottom: solid 1px var(--v-primary-lighten5);
  padding: 0 25px 0 14px;
}

.btn-custom-add {
  box-sizing: border-box;
  border-radius: 6px;
  width: 100%;
  padding-top: 1px;
  padding-bottom: 1px;

  &.theme--light {
    background: #f9f6ff !important;
    border: 1px dashed #e2e6ef;
    color: #7e5dd3;
  }

  &.theme--dark {
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    background: #3a3657 !important;
    border: 1px dashed #7e5dd3;
  }
}

.btn-custom-del {
  &.theme--dark.v-btn.v-btn--icon {
    height: auto !important;
    width: auto !important;
  }
  &.theme--light.v-btn.v-btn--icon {
    height: auto !important;
    width: auto !important;
  }

  span {
    opacity: 1 !important;
  }
}

.action-icon {
  cursor: pointer;
}
</style>
